import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useDataRefresh, DEFAULT_POLL_INTERVAL_MS } from '@/hooks/useDataRefresh';
import { Plus, Search, Calendar, Edit, Clock, MapPin } from 'lucide-react';
import { EventFormDialog } from '@/components/Events/EventFormDialog';
import type { Event } from '@/types/legacy';
import { format, isToday, isFuture } from 'date-fns';
import { es } from 'date-fns/locale';

export default function EventsPage() {
  const { userRole, user } = useAuth();
  const { toast } = useToast();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const fetchEvents = async () => {
    try {
      const { data, error } = await api
        .from('events')
        .select('*')
        .order('start_date', { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los eventos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);
  useDataRefresh(fetchEvents, { pollIntervalMs: DEFAULT_POLL_INTERVAL_MS });

  const handleEdit = (event: Event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedEvent(null);
    setDialogOpen(true);
  };

  const canManage = userRole === 'admin' || userRole === 'supervisor';
  const visibleEvents = events.filter((event) => {
    if (canManage) return true;
    const isGeneral = event.is_general ?? true;
    if (isGeneral) return true;
    const allowedUsers = Array.isArray(event.visible_to_user_ids) ? event.visible_to_user_ids : [];
    return Boolean(user?.id && allowedUsers.includes(user.id));
  });

  const filteredEvents = visibleEvents.filter(event =>
    event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getEventTypeBadge = (eventType: string) => {
    const typeConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      maintenance: { label: 'Mantenimiento', variant: 'outline' },
      meeting: { label: 'Reunión', variant: 'default' },
      deadline: { label: 'Fecha Límite', variant: 'destructive' },
      training: { label: 'Capacitación', variant: 'secondary' },
      production: { label: 'Producción', variant: 'default' },
      other: { label: 'Otro', variant: 'secondary' },
    };
    const config = typeConfig[eventType] || typeConfig.other;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      scheduled: { label: 'Programado', variant: 'outline' },
      in_progress: { label: 'En Progreso', variant: 'default' },
      completed: { label: 'Completado', variant: 'secondary' },
      cancelled: { label: 'Cancelado', variant: 'destructive' },
    };
    const config = statusConfig[status] || statusConfig.scheduled;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const scheduledEvents = visibleEvents.filter(e => e.status === 'scheduled');
  const todayEvents = visibleEvents.filter(e => isToday(new Date(e.start_date)));

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Eventos y Calendario
            </h1>
            <p className="text-muted-foreground mt-2">
              Gestión de eventos, reuniones y recordatorios
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="bg-gradient-magma hover:shadow-magma-hover">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Evento
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Buscar eventos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Eventos</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {visibleEvents.length}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Programados</p>
                <p className="text-3xl font-bold text-primary">
                  {scheduledEvents.length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Hoy</p>
                <p className="text-3xl font-bold text-success">
                  {todayEvents.length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredEvents.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Calendar className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm ? 'No se encontraron eventos' : 'No hay eventos programados'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map((event) => {
              const eventDate = new Date(event.start_date);
              const isTodayEvent = isToday(eventDate);
              const isGeneralEvent = event.is_general ?? true;

              return (
                <Card key={event.id} className={`glass-card p-6 hover:shadow-magma transition-all ${isTodayEvent ? 'border-2 border-primary' : ''}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Calendar className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{event.title}</h3>
                        {isTodayEvent && (
                          <Badge className="mt-1">Hoy</Badge>
                        )}
                      </div>
                    </div>
                    {!event.is_active && (
                      <Badge variant="secondary" className="text-xs">
                        Inactivo
                      </Badge>
                    )}
                  </div>

                  {event.description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {event.description}
                    </p>
                  )}

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      {getEventTypeBadge(event.event_type)}
                      {getStatusBadge(event.status)}
                      {!isGeneralEvent && (
                        <Badge variant="outline" className="text-xs">
                          Personalizado
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>
                        {event.all_day
                          ? format(eventDate, "dd MMM yyyy - 'Todo el día'", { locale: es })
                          : format(eventDate, 'dd MMM yyyy HH:mm', { locale: es })}
                      </span>
                    </div>

                    {event.location && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        <span>{event.location}</span>
                      </div>
                    )}

                    {event.priority > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Prioridad:</span>
                        <div className="flex gap-1">
                          {Array.from({ length: event.priority }).map((_, i) => (
                            <div key={i} className="w-2 h-2 rounded-full bg-primary" />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {canManage && (
                    <div className="mt-4 pt-4 border-t border-border/50">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(event)}
                        className="w-full"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <EventFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        event={selectedEvent}
        onSuccess={fetchEvents}
      />
    </DashboardLayout>
  );
}
